import { 
  collection, 
  getDocs, 
  getDoc, 
  doc, 
  setDoc, 
  query, 
  where, 
  orderBy, 
  limit 
} from 'firebase/firestore';
import { firestore } from '../firebase/config';

/**
 * ApplicationService - Centralized service for all job application operations
 * This service handles application submission, status checking, and management
 * with proper authentication and data validation
 */
export class ApplicationService {
  /**
   * Submit a job application
   * @param {Object} applicationData - Application data
   * @param {string} applicationData.userId - User ID
   * @param {string} applicationData.jobId - Job ID
   * @param {string} applicationData.businessId - Business ID
   * @param {Object} applicationData.additionalInfo - Additional application info
   * @returns {Object} { success: boolean, message: string, applicationId: string }
   */
  static async submitApplication(applicationData) {
    const { userId, jobId, businessId, additionalInfo = {} } = applicationData;

    if (!userId || !jobId || !businessId) {
      throw new Error('Missing required application data: userId, jobId, or businessId');
    }

    try {
      console.log('📝 ApplicationService: Submitting application', { userId, jobId, businessId });
      
      // Create unique application ID
      const applicationId = `${userId}_${jobId}`;
      const applicationRef = doc(firestore, 'jobApplications', applicationId);
      
      // Check if application already exists
      const existingApplication = await getDoc(applicationRef);
      if (existingApplication.exists()) {
        return {
          success: false,
          message: 'You have already applied for this job',
          applicationId
        };
      }
      
      // Create application document
      const timestamp = new Date();
      const applicationDoc = {
        userId,
        jobId,
        businessId,
        status: 'pending',
        appliedAt: timestamp,
        updatedAt: timestamp,
        ...additionalInfo
      };
      
      await setDoc(applicationRef, applicationDoc);
      
      console.log('✅ ApplicationService: Application submitted successfully');
      
      return {
        success: true,
        message: 'Application submitted successfully!',
        applicationId
      };
      
    } catch (error) {
      console.error('❌ ApplicationService: Error submitting application:', error);
      throw new Error(`Failed to submit application: ${error.message}`);
    }
  }

  /**
   * Check if user has applied for a specific job
   * @param {string} userId - User ID
   * @param {string} jobId - Job ID
   * @returns {Object|null} Application data or null if not found
   */
  static async checkApplicationStatus(userId, jobId) {
    if (!userId || !jobId) {
      return null;
    }

    try {
      const applicationId = `${userId}_${jobId}`;
      const applicationRef = doc(firestore, 'jobApplications', applicationId);
      const applicationDoc = await getDoc(applicationRef);
      
      if (applicationDoc.exists()) {
        return {
          id: applicationId,
          ...applicationDoc.data()
        };
      }
      
      return null;
      
    } catch (error) {
      console.error('❌ ApplicationService: Error checking application status:', error);
      return null;
    }
  }

  /**
   * Batch check application status for multiple jobs
   * @param {string} userId - User ID
   * @param {Array<string>} jobIds - Array of job IDs
   * @returns {Set<string>} Set of job IDs that user has applied to
   */
  static async batchCheckApplicationStatus(userId, jobIds) {
    if (!userId || !jobIds || jobIds.length === 0) {
      return new Set();
    }

    try {
      console.log(`🔍 ApplicationService: Checking applications for ${jobIds.length} jobs`);
      
      const appliedJobIds = new Set();
      
      // Check applications in batches to avoid overwhelming Firestore
      const batchSize = 10;
      for (let i = 0; i < jobIds.length; i += batchSize) {
        const batchJobIds = jobIds.slice(i, i + batchSize);
        
        // Check each job individually due to compound key structure
        const batchPromises = batchJobIds.map(async (jobId) => {
          const applicationId = `${userId}_${jobId}`;
          const applicationRef = doc(firestore, 'jobApplications', applicationId);
          
          try {
            const applicationDoc = await getDoc(applicationRef);
            if (applicationDoc.exists()) {
              appliedJobIds.add(jobId);
            }
          } catch (error) {
            console.warn(`⚠️ Error checking application for job ${jobId}:`, error);
          }
        });
        
        await Promise.all(batchPromises);
      }
      
      console.log(`✅ ApplicationService: Found ${appliedJobIds.size} existing applications`);
      return appliedJobIds;
      
    } catch (error) {
      console.error('❌ ApplicationService: Error in batch check:', error);
      return new Set();
    }
  }

  /**
   * Get all applications for a user
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @param {string} options.status - Filter by status ('pending', 'accepted', 'rejected')
   * @param {number} options.limit - Maximum number of applications to return
   * @returns {Array} Array of user applications
   */
  static async getUserApplications(userId, options = {}) {
    if (!userId) {
      return [];
    }

    const { status, limit: queryLimit = 50 } = options;

    try {
      console.log(`🔍 ApplicationService: Fetching applications for user ${userId}`);
      
      const applicationsRef = collection(firestore, 'jobApplications');
      
      // Use existing index structure (userId, appliedAt) and filter client-side
      // This avoids needing additional composite indexes
      const applicationsQuery = query(
        applicationsRef,
        where('userId', '==', userId),
        orderBy('appliedAt', 'desc'),
        limit(queryLimit)
      );
      
      const snapshot = await getDocs(applicationsQuery);
      
      let applications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply client-side status filtering if needed
      if (status) {
        applications = applications.filter(app => app.status === status);
        console.log(`🔍 ApplicationService: Filtered to ${applications.length} applications with status '${status}'`);
      }
      
      console.log(`✅ ApplicationService: Found ${applications.length} applications for user`);
      return applications;
      
    } catch (error) {
      console.error('❌ ApplicationService: Error fetching user applications:', error);
      throw new Error(`Failed to fetch user applications: ${error.message}`);
    }
  }

  /**
   * Get all applications for a business
   * @param {string} businessId - Business ID
   * @param {Object} options - Query options
   * @param {string} options.status - Filter by status
   * @param {number} options.limit - Maximum results
   * @returns {Array} Array of applications for the business
   */
  static async getBusinessApplications(businessId, options = {}) {
    if (!businessId) {
      return [];
    }

    const { status, limit: queryLimit = 100 } = options;

    try {
      console.log(`🔍 ApplicationService: Fetching applications for business ${businessId}`);
      
      const applicationsRef = collection(firestore, 'jobApplications');
      
      // Use existing index structure and filter client-side to avoid index issues
      const applicationsQuery = query(
        applicationsRef,
        where('businessId', '==', businessId),
        orderBy('appliedAt', 'desc'),
        limit(queryLimit)
      );
      
      const snapshot = await getDocs(applicationsQuery);
      
      let applications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Apply client-side status filtering if needed
      if (status) {
        applications = applications.filter(app => app.status === status);
        console.log(`🔍 ApplicationService: Filtered to ${applications.length} business applications with status '${status}'`);
      }
      
      console.log(`✅ ApplicationService: Found ${applications.length} applications for business`);
      return applications;
      
    } catch (error) {
      console.error('❌ ApplicationService: Error fetching business applications:', error);
      throw new Error(`Failed to fetch business applications: ${error.message}`);
    }
  }

  /**
   * Update application status
   * @param {string} applicationId - Application ID
   * @param {string} newStatus - New status ('pending', 'accepted', 'rejected', 'withdrawn')
   * @param {string} updatedBy - ID of user making the update
   * @returns {Object} { success: boolean, message: string }
   */
  static async updateApplicationStatus(applicationId, newStatus, updatedBy) {
    if (!applicationId || !newStatus || !updatedBy) {
      throw new Error('Missing required parameters for status update');
    }

    const validStatuses = ['pending', 'accepted', 'rejected', 'withdrawn'];
    if (!validStatuses.includes(newStatus)) {
      throw new Error(`Invalid status: ${newStatus}`);
    }

    try {
      console.log(`📝 ApplicationService: Updating application ${applicationId} to ${newStatus}`);
      
      const applicationRef = doc(firestore, 'jobApplications', applicationId);
      
      // Check if application exists
      const applicationDoc = await getDoc(applicationRef);
      if (!applicationDoc.exists()) {
        return {
          success: false,
          message: 'Application not found'
        };
      }
      
      // Update application
      const updateData = {
        status: newStatus,
        updatedAt: new Date(),
        updatedBy
      };
      
      await setDoc(applicationRef, updateData, { merge: true });
      
      console.log('✅ ApplicationService: Application status updated successfully');
      
      return {
        success: true,
        message: `Application status updated to ${newStatus}`
      };
      
    } catch (error) {
      console.error('❌ ApplicationService: Error updating application status:', error);
      throw new Error(`Failed to update application status: ${error.message}`);
    }
  }

  /**
   * Withdraw an application
   * @param {string} userId - User ID
   * @param {string} jobId - Job ID
   * @returns {Object} { success: boolean, message: string }
   */
  static async withdrawApplication(userId, jobId) {
    if (!userId || !jobId) {
      throw new Error('Missing required parameters for withdrawal');
    }

    try {
      const applicationId = `${userId}_${jobId}`;
      
      return await this.updateApplicationStatus(applicationId, 'withdrawn', userId);
      
    } catch (error) {
      console.error('❌ ApplicationService: Error withdrawing application:', error);
      throw new Error(`Failed to withdraw application: ${error.message}`);
    }
  }

  /**
   * Get application statistics for a user
   * @param {string} userId - User ID
   * @returns {Object} Statistics object with counts by status
   */
  static async getUserApplicationStats(userId) {
    if (!userId) {
      return { total: 0, pending: 0, accepted: 0, rejected: 0, withdrawn: 0 };
    }

    try {
      console.log(`📊 ApplicationService: Getting stats for user ${userId}`);
      
      const applications = await this.getUserApplications(userId, { limit: 1000 });
      
      const stats = {
        total: applications.length,
        pending: 0,
        accepted: 0,
        rejected: 0,
        withdrawn: 0
      };
      
      applications.forEach(app => {
        if (app.status && stats.hasOwnProperty(app.status)) {
          stats[app.status]++;
        }
      });
      
      console.log('✅ ApplicationService: User stats calculated:', stats);
      return stats;
      
    } catch (error) {
      console.error('❌ ApplicationService: Error getting user stats:', error);
      throw new Error(`Failed to get user application statistics: ${error.message}`);
    }
  }

  /**
   * Get application statistics for a business
   * @param {string} businessId - Business ID
   * @returns {Object} Statistics object with counts by status
   */
  static async getBusinessApplicationStats(businessId) {
    if (!businessId) {
      return { total: 0, pending: 0, accepted: 0, rejected: 0, withdrawn: 0 };
    }

    try {
      console.log(`📊 ApplicationService: Getting stats for business ${businessId}`);
      
      const applications = await this.getBusinessApplications(businessId, { limit: 1000 });
      
      const stats = {
        total: applications.length,
        pending: 0,
        accepted: 0,
        rejected: 0,
        withdrawn: 0
      };
      
      applications.forEach(app => {
        if (app.status && stats.hasOwnProperty(app.status)) {
          stats[app.status]++;
        }
      });
      
      console.log('✅ ApplicationService: Business stats calculated:', stats);
      return stats;
      
    } catch (error) {
      console.error('❌ ApplicationService: Error getting business stats:', error);
      throw new Error(`Failed to get business application statistics: ${error.message}`);
    }
  }

  /**
   * Handle external application URL click (for tracking purposes)
   * @param {string} userId - User ID
   * @param {string} jobId - Job ID
   * @param {string} applicationUrl - External URL
   * @returns {Object} { success: boolean, message: string }
   */
  static async handleExternalApplication(userId, jobId, applicationUrl) {
    if (!userId || !jobId || !applicationUrl) {
      return { success: false, message: 'Missing required parameters' };
    }

    try {
      console.log('📝 ApplicationService: Tracking external application click');
      
      // Create a tracking record for external applications
      const trackingId = `${userId}_${jobId}_external`;
      const trackingRef = doc(firestore, 'externalApplicationTracking', trackingId);
      
      const trackingData = {
        userId,
        jobId,
        applicationUrl,
        clickedAt: new Date(),
        type: 'external_redirect'
      };
      
      await setDoc(trackingRef, trackingData, { merge: true });
      
      // Open external URL
      window.open(applicationUrl, '_blank');
      
      return {
        success: true,
        message: 'Redirected to external application page'
      };
      
    } catch (error) {
      console.error('❌ ApplicationService: Error handling external application:', error);
      // Still open the URL even if tracking fails
      window.open(applicationUrl, '_blank');
      return {
        success: true,
        message: 'Redirected to external application page'
      };
    }
  }
}

export default ApplicationService; 